import { createHash } from 'node:crypto'
import { glob, mkdir, mkdtemp, readFile, rm, stat, writeFile } from 'node:fs/promises'
import { tmpdir } from 'node:os'
import { dirname, join, relative, resolve, sep } from 'node:path'
import * as tar from 'tar'
import { signEnvelope } from '../crypto/dsse.js'
import type { SigningKey } from '../crypto/keys.js'
import {
  encryptBytes,
  generateContentKey,
  wrapContentKey,
  CONTENT_ALGORITHM,
  WRAP_ALGORITHM,
  type EncryptionManifest,
} from '../crypto/seal.js'
import { loadManifest, normalizeKind, validatePackageName, type Manifest } from './manifest.js'

export interface PackRecipient {
  id: string
  /** Base64 raw X25519 public key. */
  publicKey: string
}

export interface PackOptions {
  manifestPath: string
  publisher: string
  /** Overrides packaging.name / metadata.name. */
  name?: string
  outDir?: string
  signingKey?: SigningKey
  /** When set, payload entries are AES-256-GCM encrypted and the content key wrapped per recipient. */
  encryptFor?: PackRecipient[]
}

export interface PackResult {
  outFile: string
  name: string
  publisher: string
  version: string
  kind: string
  files: string[]
  treeDigest: string
  signed: boolean
  encrypted: boolean
}

/**
 * Builds a .vpkg: gzip tarball with manifest.yaml, files/**, and .verentis metadata
 * (package.yaml, digests.json, signatures/developer.dsse.json). Layout and digest
 * semantics must match the registry's VpkgArchiveService.
 */
export async function pack(options: PackOptions): Promise<PackResult> {
  const manifestPath = resolve(options.manifestPath)
  const projectDir = dirname(manifestPath)
  const manifest = await loadManifest(manifestPath)
  const kind = normalizeKind(manifest.kind)

  const name = (options.name ?? manifest.packaging?.name ?? manifest.metadata.name).toLowerCase()
  const publisher = options.publisher.toLowerCase()
  validatePackageName(name, 'package name')
  validatePackageName(publisher, 'publisher name')
  const version = manifest.metadata.version

  const payloadFiles = await resolvePayloadFiles(projectDir, manifestPath, manifest)

  const staging = await mkdtemp(join(tmpdir(), 'vpkg-'))
  try {
    const manifestText = await readFile(manifestPath, 'utf8')
    await writeFile(join(staging, 'manifest.yaml'), manifestText)

    const encrypt = (options.encryptFor?.length ?? 0) > 0
    const contentKey = encrypt ? generateContentKey() : null

    const digests: Record<string, string> = {
      'manifest.yaml': sha256Hex(Buffer.from(manifestText, 'utf8')),
    }

    for (const file of payloadFiles) {
      const sourcePath = join(projectDir, file)
      const entryName = `files/${toEntryPath(file)}`
      const targetPath = join(staging, 'files', file)
      await mkdir(dirname(targetPath), { recursive: true })

      // Encrypted packages seal every payload entry; digests cover the sealed bytes so
      // registry integrity + signing are unchanged.
      const bytes = contentKey ? encryptBytes(contentKey, await readFile(sourcePath)) : await readFile(sourcePath)
      await writeFile(targetPath, bytes)
      digests[entryName] = sha256Hex(bytes)
    }

    await mkdir(join(staging, '.verentis', 'signatures'), { recursive: true })

    if (contentKey) {
      const encryption: EncryptionManifest = {
        algorithm: CONTENT_ALGORITHM,
        keyWrapping: WRAP_ALGORITHM,
        recipients: options.encryptFor!.map((r) => wrapContentKey(contentKey, r.id, r.publicKey)),
      }
      await writeFile(join(staging, '.verentis', 'encryption.json'), JSON.stringify(encryption, null, 2))
    }

    const digestsJson = JSON.stringify({ algorithm: 'sha256', entries: digests }, null, 2)
    await writeFile(join(staging, '.verentis', 'digests.json'), digestsJson)

    const packageYaml = [
      `name: ${name}`,
      `publisher: ${publisher}`,
      `version: ${version}`,
      `kind: ${kind}`,
      ...(manifest.metadata['display-name'] ? [`display-name: ${JSON.stringify(manifest.metadata['display-name'])}`] : []),
      '',
    ].join('\n')
    await writeFile(join(staging, '.verentis', 'package.yaml'), packageYaml)

    let signed = false
    if (options.signingKey) {
      const envelope = signEnvelope(digestsJson, options.signingKey)
      await writeFile(join(staging, '.verentis', 'signatures', 'developer.dsse.json'), JSON.stringify(envelope, null, 2))
      signed = true
    }

    const outDir = resolve(options.outDir ?? projectDir)
    await mkdir(outDir, { recursive: true })
    const outFile = join(outDir, `${name}-${version}.vpkg`)

    const entries = [
      'manifest.yaml',
      '.verentis/package.yaml',
      '.verentis/digests.json',
      ...(contentKey ? ['.verentis/encryption.json'] : []),
      ...(signed ? ['.verentis/signatures/developer.dsse.json'] : []),
      ...payloadFiles.map((file) => `files/${toEntryPath(file)}`),
    ]
    await tar.create({ gzip: true, file: outFile, cwd: staging, portable: true }, entries)

    return {
      outFile,
      name,
      publisher,
      version,
      kind,
      files: payloadFiles.map(toEntryPath),
      treeDigest: sha256Hex(Buffer.from(digestsJson, 'utf8')),
      signed,
      encrypted: encrypt,
    }
  } finally {
    await rm(staging, { recursive: true, force: true })
  }
}

async function resolvePayloadFiles(projectDir: string, manifestPath: string, manifest: Manifest): Promise<string[]> {
  const includes = manifest.packaging?.files ?? defaultIncludes(manifest)
  const excludes = manifest.packaging?.exclude ?? []

  const matched = new Set<string>()
  for (const pattern of includes) {
    for await (const entry of glob(pattern, { cwd: projectDir, exclude: excludes })) {
      const abs = join(projectDir, entry)
      if (!(await stat(abs)).isFile()) continue
      if (abs === manifestPath) continue
      const rel = relative(projectDir, abs)
      if (rel.startsWith('..')) throw new Error(`Packaging glob '${pattern}' matched '${rel}' outside the project directory.`)
      matched.add(rel)
    }
  }
  return [...matched].sort()
}

function defaultIncludes(manifest: Manifest): string[] {
  // Without an explicit packaging.files list, pull in every file the manifest references:
  // schema `source:` paths (the common Application case) and every `spec.install[].from` glob
  // (apps that place files into the workspace VFS). Engines/bundles without either must declare
  // packaging.files.
  const sources = new Set<string>()
  collectSchemaSources(manifest, sources)
  collectInstallSources(manifest, sources)
  if (sources.size === 0) return []
  return [...sources]
}

/** Collects `spec.install[].from` globs so a file-collection app packs its payload by default. */
function collectInstallSources(manifest: Manifest, sources: Set<string>): void {
  const spec = (manifest as { spec?: { install?: unknown } }).spec
  const install = spec?.install
  if (!Array.isArray(install)) return
  for (const directive of install) {
    const from = (directive as { from?: unknown })?.from
    if (typeof from === 'string' && from.length > 0 && !from.startsWith('/')) {
      sources.add(from)
    }
  }
}

function collectSchemaSources(node: unknown, sources: Set<string>): void {
  if (Array.isArray(node)) {
    for (const item of node) collectSchemaSources(item, sources)
    return
  }
  if (node && typeof node === 'object') {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      if (key === 'source' && typeof value === 'string' && value.startsWith('./')) {
        sources.add(value.slice(2))
      } else {
        collectSchemaSources(value, sources)
      }
    }
  }
}

function toEntryPath(file: string): string {
  return file.split(sep).join('/')
}

function sha256Hex(data: Buffer): string {
  return createHash('sha256').update(data).digest('hex')
}
